import { ColumnType } from '~shared/rundown/column'
import type {
  ColumnCreate,
  ColumnPatch,
  ColumnSerialized,
} from '~shared/rundown/column'
import type { Cell, Rundown } from '~shared/types'

import { rundownApi } from '../../../axios'
import { apiErrorHandler } from '../../../utils/api-error-handler'

type AddColumnDto = [Rundown, ColumnSerialized]

/**
 * Add a default public column. The server fills every field from the schema
 * defaults.
 */
export const addColumn = async ({
  rundownId,
  params,
}: {
  rundownId: string
  params?: {
    after?: string
  }
}) => {
  return await apiErrorHandler(
    rundownApi.post<AddColumnDto>(`/${rundownId}/columns`, undefined, {
      params,
    }),
  )
}

/**
 * Create the user's own "Private notes" column.
 */
export const addPrivateNotesColumn = async ({
  rundownId,
  privateUid,
}: {
  rundownId: string
  privateUid: string
}) => {
  return await apiErrorHandler(
    rundownApi.post<AddColumnDto>(`/${rundownId}/private-columns`, {
      name: 'Private notes',
      // TODO: Think of introducing the new type for custom column types
      // columnType: 'PRIVATE_NOTES',
      type: ColumnType.RICHTEXT,
      privateUid,
    } satisfies ColumnCreate),
  )
}

export const updateColumn = (
  {
    rundownId,
    columnId,
    payload,
  }: {
    rundownId: string
    columnId: string
    payload: ColumnPatch
  },
  { toast } = { toast: true },
) => {
  return apiErrorHandler(
    rundownApi.patch<ColumnSerialized>(
      `/${rundownId}/columns/${columnId}`,
      payload,
    ),
    { toast },
  )
}

/**
 * Patch the user's own private column. Needs only a READ token: the server
 * checks the column belongs to the caller.
 */
export const updatePrivateColumn = ({
  rundownId,
  columnId,
  payload,
}: {
  rundownId: string
  columnId: string
  payload: ColumnPatch
}) => {
  return apiErrorHandler(
    rundownApi.patch<ColumnSerialized>(
      `/${rundownId}/private-columns/${columnId}`,
      payload,
    ),
  )
}

export const reorderColumns = async ({
  rundownId,
  columnIds,
}: {
  rundownId: string
  columnIds: string[]
}) => {
  return await apiErrorHandler(
    rundownApi.patch(`/${rundownId}/reorderColumns`, columnIds),
  )
}

export const reorderPrivateColumn = async ({
  rundownId,
  columnId,
  payload,
}: {
  rundownId: string
  columnId: string
  payload?: {
    /** `null` or missing moves the column to the start */
    afterColumnId?: string | null
  }
}) => {
  return await apiErrorHandler(
    rundownApi.patch(`/${rundownId}/private-columns/${columnId}/move`, payload),
  )
}

export const deleteColumn = async ({
  rundownId,
  columnId,
}: {
  rundownId: string
  columnId: string
}) => {
  return apiErrorHandler(rundownApi.delete(`/${rundownId}/columns/${columnId}`))
}

export const clearPrivateColumn = async ({
  rundownId,
  columnId,
}: {
  rundownId: string
  columnId: string
}) => {
  return await apiErrorHandler(
    rundownApi.post<Cell[]>(`/${rundownId}/private-columns/${columnId}/clear`),
  )
}
